import { createFifoCache } from '@ardoq/common-helpers';
import { registerPendingImagesSource } from '@ardoq/export';
import { RepresentationData } from '@ardoq/data-model';
import { logError } from '@ardoq/logging';
import { actionCreator, dispatchAction } from '@ardoq/rxbeach';
import {
  FALLBACK_IMAGE_ICON_INFO,
  IconInfoForSvgImage,
  createSvgImageUrl,
} from '@ardoq/icons';

export const canvasResolvedImages = new Map<string, HTMLImageElement>();
/**
 * Component images that have been requested but have not loaded, keyed by url:
 * the renderers ask on every repaint, so an unresolved one is asked for again
 * each frame. Renderers skip drawing them, so nothing else records that the
 * canvas is waiting on them.
 */
const pendingCanvasImages = new Map<string, HTMLImageElement>();
const pendingSvgImages = new Set<HTMLImageElement>();
const PENDING_IMAGE_TIMEOUT_MS = 30_000;
export const getPendingCanvasImages = (): HTMLImageElement[] => [
  ...pendingCanvasImages.values(),
  ...pendingSvgImages,
];
registerPendingImagesSource(getPendingCanvasImages);
/** dispatched when an image url is loaded in the browser, necessitating a redraw. */
export const canvasResolvedImageReady = actionCreator(
  '[Canvas] RESOLVED_IMAGE_READY'
);
/** dispatched when an SVG image is loaded in the browser, necessitating a redraw. */
export const svgImageReady = actionCreator('[Canvas] SVG_IMAGE_READY');

/**
 * Cache for svg elements to avoid cloning sub-elements of icons.
 */
const svgElementCache = new Map<string, SVGSVGElement>();
const dispatchSvgImageReady = () => dispatchAction(svgImageReady());
/** args interpolation: `${assetId}~~${color}~~${fill}~~${stroke}~~${width}~~${height}~~${negativeColor}` */
export const svgImage = createFifoCache(
  Infinity,
  (iconInfo: IconInfoForSvgImage) => {
    const url = createSvgImageUrl(iconInfo, svgElementCache);
    if (url === '') {
      logError(Error('unresolved svg image'));
    }
    const result = new Image();
    result.src = url;
    if (!result.complete) {
      pendingSvgImages.add(result);
      const settle = () => pendingSvgImages.delete(result);
      result.addEventListener('load', settle, { once: true });
      result.addEventListener('error', settle, { once: true });
      result.addEventListener('load', dispatchSvgImageReady, { once: true });
    }
    return result;
  }
);

export const FALLBACK_IMAGE = svgImage(FALLBACK_IMAGE_ICON_INFO);

const setCanvasResolvedImage = (
  representationData: RepresentationData,
  image: HTMLImageElement
) => {
  canvasResolvedImages.set(representationData.value!, image);
  dispatchAction(canvasResolvedImageReady());
};

// attempting to draw incomplete image to the canvas will produce blankness.
// if the image is complete, this indicates the browser has downloaded it.
// otherwise, we must wait for onload and dispatch this event to the view, so it can redraw itself when the image is ready.
export const canvasResolveImage = (representationData: RepresentationData) => {
  const { isImage, value } = representationData;
  if (!isImage || !value) return;
  if (canvasResolvedImages.has(value) || pendingCanvasImages.has(value)) return;

  const img = new Image();
  pendingCanvasImages.set(value, img);
  const resolve = (image: HTMLImageElement) => {
    clearTimeout(giveUp);
    pendingCanvasImages.delete(value);
    setCanvasResolvedImage(representationData, image);
  };
  // A request the browser holds open fires neither handler, and nothing else
  // drops the entry — which would leave every later export unable to settle. The
  // fallback is cached rather than retried, because re-requesting would put the
  // url straight back into the map this deadline exists to drain.
  const giveUp = setTimeout(
    () => resolve(FALLBACK_IMAGE),
    PENDING_IMAGE_TIMEOUT_MS
  );
  img.onload = () => resolve(img);
  img.onerror = () => resolve(FALLBACK_IMAGE);

  img.src = value;
};

const MAX_EXPORT_REDRAWS = 5;
const IMAGE_WAIT_CAP_MS = 1_000;

const whenImageSettles = (image: HTMLImageElement): Promise<void> =>
  new Promise(resolve => {
    image.addEventListener('load', () => resolve(), { once: true });
    image.addEventListener('error', () => resolve(), { once: true });
  });

const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/** Redraws until the images the canvas asked for have loaded, so a copy of it shows them. */
export const redrawUntilCanvasImagesLoad = async (
  redraw: VoidFunction
): Promise<void> => {
  for (let pass = 0; pass < MAX_EXPORT_REDRAWS; pass++) {
    redraw();
    const pending = getPendingCanvasImages();
    if (pending.length === 0) {
      return;
    }
    await Promise.race([
      Promise.all(pending.map(whenImageSettles)),
      sleep(IMAGE_WAIT_CAP_MS),
    ]);
  }
  redraw();
};
